/**
 * Converters between Supabase row shape and the UI's stable view-model shape.
 * Keeps pages decoupled from schema changes — only this file moves when
 * column names shift.
 *
 * UI fields the existing pages rely on:
 *   id, title, category, author_name, preview, created_at,
 *   story.{before,struggle,turning_point,what_helped,outcome},
 *   sections (array of {label, content}),
 *   tags (life_areas | inner_knots | obstacles | mental_states | inner_qualities | guna_arc),
 *   similarity_score (search only),
 *   sharing_permissions, media (legacy — undefined for now)
 */

const truncate = (text, n = 240) => {
  if (!text) return '';
  return text.length > n ? `${text.slice(0, n).trim()}\u2026` : text;
};

const computePreview = (row) => {
  // Author-curated highlight wins — it's the 2-3 line hook the author wrote
  // on the review screen specifically to draw readers in. Auto-derived
  // excerpts are only a fallback for older rows that pre-date the highlight
  // field or for stories where the author left it blank.
  const highlight = row.highlight?.trim();
  if (highlight) return truncate(highlight);
  if (row.section_1) return truncate(row.section_1);
  if (row.raw_text) return truncate(row.raw_text);
  for (let i = 2; i <= 5; i += 1) {
    if (row[`section_${i}`]) return truncate(row[`section_${i}`]);
  }
  return '';
};

const buildSections = (row) => {
  const out = [];
  for (let i = 1; i <= 5; i += 1) {
    const content = row[`section_${i}`];
    const label = row[`section_${i}_label`];
    if (content && label) {
      out.push({ key: `section_${i}`, label, content });
    }
  }
  return out;
};

/**
 * Row from RPC `km_list_my_stories` — shelf cards (includes moderation status).
 */
export const toMyStoryListItemDto = (row) => {
  if (!row) return null;
  const n = row.reader_report_count;
  const reader_report_count =
    n != null && n !== '' ? Number(n) : null;
  return {
    id: row.id,
    title: row.title || 'Untitled',
    story_type: row.story_type,
    story_type_display_name: row.story_type_display_name || row.story_type,
    status: row.status,
    created_at: row.created_at,
    preview: truncate(row.preview_raw || '', 240),
    is_anonymous: row.is_anonymous,
    author_name: row.author_label || 'Anonymous',
    moderator_notes: row.moderator_notes?.trim() || null,
    reader_report_count: Number.isFinite(reader_report_count) ? reader_report_count : null,
  };
};

/**
 * Map a row from public.km_public_stories (joined with km_story_types) to the
 * shape the existing UI consumes. Also accepts `km_get_my_story_for_edit` rows.
 */
export const toPublicStoryDto = (row) => {
  if (!row) return null;
  const sections = buildSections(row);
  // Byline fallback chain. Live profile fields (pen_name / display_name)
  // come from the km_user_profiles join in km_public_stories — so renames
  // flow through to past stories automatically. The snapshot
  // `author_display_name` (stored on km_stories at submission) is kept
  // only as a fallback for legacy rows authored before profiles existed.
  const authorName =
    row.author_pen_name ||
    row.author_display_name_profile ||
    row.author_display_name ||
    'Anonymous';
  const dto = {
    id: row.id,
    title: row.title,
    highlight: row.highlight || null,
    category: row.tradition || row.story_type || 'Story',
    author_name: authorName,
    // Joined-in author profile fields. NULL for anonymous stories — the
    // view masks them server-side, so the UI never sees identifying data
    // for is_anonymous = true rows. No author UUID is exposed.
    author_avatar_object_key: row.author_avatar_object_key || null,
    author_bio: row.author_bio || null,
    is_anonymous: row.is_anonymous,
    created_at: row.created_at,
    story_type: row.story_type,
    sections,
    // Legacy 5-section shape used by current StoryViewPage. Each may be
    // undefined for non-personal types — UI handles null gracefully.
    story: {
      before: row.section_1 || '',
      struggle: row.section_2 || '',
      turning_point: row.section_3 || '',
      what_helped: row.section_4 || '',
      outcome: row.section_5 || '',
    },
    section_labels: {
      section_1: row.section_1_label,
      section_2: row.section_2_label,
      section_3: row.section_3_label,
      section_4: row.section_4_label,
      section_5: row.section_5_label,
    },
    raw_text: row.raw_text,
    life_areas:      row.life_areas      || [],
    inner_knots:     row.inner_knots     || [],
    obstacles:       row.obstacles       || [],
    mental_states:   row.mental_states   || [],
    inner_qualities: row.inner_qualities || [],
    guna_arc:        row.guna_arc        || [],
    professions:     row.professions     || [],
    tradition: row.tradition,
    practice_ids: row.practice_ids || [],
    practice_freetext: row.practice_freetext,
    time_period: row.time_period,
    preview: computePreview(row),
    // Legacy fields not in v2 schema — keep undefined so optional chaining in
    // existing UI silently hides them. Will be re-introduced when media/
    // sharing_permissions land.
    media: undefined,
    sharing_permissions: undefined,
  };
  if (row.status != null) dto.status = row.status;
  const n = row.reader_report_count;
  const reader_report_count =
    n != null && n !== '' ? Number(n) : null;
  if (Number.isFinite(reader_report_count)) dto.reader_report_count = reader_report_count;
  const mn = row.moderator_notes?.trim();
  if (mn) dto.moderator_notes = mn;
  return dto;
};

/**
 * Map a row from km_search_stories RPC to the same UI shape, plus similarity.
 */
export const toSearchResultDto = (row) => {
  const dto = toPublicStoryDto(row);
  if (!dto) return null;
  // similarity is 0 for FTS-only results (no embedding match above threshold).
  // Preserve the raw value so the UI can distinguish "keyword match" from
  // "semantic match" rather than showing a misleading "0% match".
  dto.similarity_score = typeof row.similarity === 'number' ? row.similarity : null;
  dto.rrf_score = typeof row.rrf_score === 'number' ? row.rrf_score : null;
  return dto;
};

/**
 * Map an internal row (km_stories) — used by ModerationDashboard — to UI shape.
 * Differs from public DTO because moderators can see raw_text + author_id.
 */
export const toModerationStoryDto = (row) => {
  const dto = toPublicStoryDto(row);
  if (!dto) return null;
  dto.author_id = row.author_id;
  dto.status = row.status;
  return dto;
};

/**
 * Row from `km_story_reports` with embedded `km_stories` (moderator SELECT + join).
 */
export const toStoryReportDto = (row) => {
  if (!row) return null;
  const story = row.km_stories;
  return {
    id: row.id,
    story_id: row.story_id,
    reporter_id: row.reporter_id,
    reason: row.reason,
    details: row.details || '',
    status: row.status,
    created_at: row.created_at,
    story_title: story?.title || 'Unknown story',
    story_status: story?.status,
    story_author_name: story?.author_display_name || 'Anonymous',
  };
};
